/* ONE ANSWER TO "WHAT DO WE ASK BRILL FOR A POST" — for every path that drafts.
 *
 * Two paths draft a Social post: the post screen (Draft it for me, Redraft,
 * Chat on an empty box) and, since 2026-09-28, the sparkle on an arc's row. The
 * lesson of 2026-09-24 was that every Momentum record has two paths to Brill
 * and context wired into one and not the other is how Sites and Courses both
 * drafted without their offer. So the request is built HERE, once, and both
 * callers hand it the same things.
 *
 * Lifted out of PostEditor's `writePost` unchanged in behaviour; the only
 * addition is `arc` — the series a post belongs to, which rides to the server
 * as-is (buildSocialUserPrompt reads it).
 */
import { SOCIAL_HOOK_CATEGORIES } from "../constants/socialHookCategories";
import { buildPostHistory, suggestNextPost } from "./socialHistory";
import { deriveTitle } from "./useSocialPosts";
import { ctaBrief } from "./useCtas";
import { offerLine } from "./offerLine";
import { hashSource } from "./sourceHash";
import { POST_LENGTHS, askLink, askOf, defaultLength, pictureOf, platformOf, withPicture } from "./socialPosts";

/**
 * @param draft   the post being written (its own settings are the fallback)
 * @param choice  what she picked in the Draft pop-up, or what an arc's slot
 *                says: { pillar, theme, hook, offerId, ask, length, idea } —
 *                "" / null for "let Brill pick". null = use the post's own.
 * @param angle   what it is about, in her words
 * @param arc     the series context from `arcContext`, or null
 * @returns { body, patchFor } — `body` is the /api/chat request minus the
 *          mode/moduleId/clientContext every Social call adds; `patchFor(written)`
 *          returns `(cur) => patch`, read at the moment the words LAND.
 */
export function postRequest({ draft, choice = null, angle = "", pillars = [], offers = [], ctas = [], posts = [], arc = null }) {
  const platform = platformOf(draft?.platform);
  const ownPillar = pillars.find((p) => p.label === draft?.inputs?.pillar) || null;
  const pick = suggestNextPost(posts, { pillars, hookIds: SOCIAL_HOOK_CATEGORIES.map((c) => c.id), platform: platform.id });
  const chosenPillar = choice ? pillars.find((p) => p.label === choice.pillar) || null : ownPillar;
  const usePillar = chosenPillar || (pick.pillarIdx != null ? pillars[pick.pillarIdx] : null);
  const chosenHook = choice ? choice.hook : draft?.inputs?.hookCategory;
  const hook = (chosenHook && SOCIAL_HOOK_CATEGORIES.some((c) => c.id === chosenHook)) ? chosenHook : pick.hookCatId;
  /* A theme belongs to its pillar: hers when she picked one, else Brill's
     quiet pick — but only if Brill's pick is the same pillar, or it would
     write a Trust post to an Attraction theme. */
  const theme = (choice?.theme && usePillar?.themes?.includes(choice.theme)) ? choice.theme
    : (usePillar && pick.pillarIdx != null && pillars[pick.pillarIdx]?.label === usePillar.label ? pick.theme || "" : "");
  const offerObj = offers.find((o) => o.id === (choice ? choice.offerId : draft?.offerId)) || null;
  let ask = choice ? (choice.ask || "none") : askOf(draft);
  if (ask === "learn" && !offerObj) ask = "none";
  const ctaRow = ask.startsWith("link:") ? (ctas.find((c) => c.id === ask.slice(5)) || null) : null;
  if (ask.startsWith("link:") && !ctaRow) ask = "none";
  const ctaKind = ctaRow ? "library" : ask;
  const length = (choice?.length || draft?.inputs?.length || defaultLength(platform.id));
  const link = askLink(ask, { offer: offerObj, ctas });

  /* Her other posts on this platform, so Brill stops repeating itself — never
     this one, or a redraft would be told to avoid the post it is rewriting.
     An arc's own posts are left out too: they arrive in `arc`, in order and
     as she finished them, and sending them twice is paying for them twice. */
  const history = buildPostHistory(posts.filter((p) => p.id !== draft?.id), { platform: platform.id, excludeArcId: draft?.arcId || null });

  const body = {
    kind: "post",
    platform: platform.id,
    pillar: usePillar ? [usePillar.label, usePillar.subtitle, usePillar.description].filter(Boolean).join("\n") : null,
    theme,
    hookCategory: hook,
    cta: ctaKind,
    ctaBrief: ctaBrief(ctaRow),
    offer: offerObj ? offerLine(offerObj) : null,
    length,
    context: angle,
    postHistory: history,
    ...(arc ? { arc } : {}),
    /* An attached picture, as her own words about it — sent AFTER the context
       in the prompt, as a filter, never the subject (her call, 2026-09-28). */
    ...(String(pictureOf(draft?.visual)?.about || "").trim() ? { picture: pictureOf(draft.visual).about.trim() } : {}),
    /* THE POST AS SHE LEFT IT, whenever there is one — a redraft, or an arc
       post's Rewrite (her catch, 2026-10-06). Without it a redraft wrote from
       the first "about" and lost every fact she had corrected by hand. Brill
       may change the words; the events in it are hers (HER_STORY_RULE). */
    ...(String(draft?.caption || "").trim() ? { currentPost: String(draft.caption) } : {}),
  };

  /* `captionDraft` is BRILL'S LAST WORDS (changed 2026-09-30, her voice
     learning: "saves a draft, then each module shows the voice changes"). It
     was Brill's FIRST full draft, never refreshed — so a later redraft, or a
     one-tap, counted as HER edit, and learning from that would teach Brill
     from its own rewrites. Every time Brill replaces the words it moves here;
     what she changes after that is hers alone. Never set on a post she wrote
     herself. */
  /* THE NAME follows the words while it is still the one made FROM the words
     — the first live arc run (2026-09-28) rewrote a post and kept the name of
     the draft it threw away. A name she typed never matches, so it is never
     touched. */
  const autoNamed = (cur) => !String(cur.title || "").trim() || cur.title === deriveTitle(cur.caption);
  const patchFor = (written) => (cur) => ({
    caption: written,
    captionDraft: written,
    ...(autoNamed(cur) ? { title: deriveTitle(written) } : {}),
    inputs: {
      ...(cur.inputs || {}), pillar: usePillar?.label || null, theme: theme || null, hookCategory: hook || null, context: angle || null, cta: ctaKind, length,
      /* WHAT IT WAS WRITTEN FROM, in an arc: the earlier posts as Brill was
         shown them, hashed. If one changes later, this post is flagged. */
      ...(arc ? { arcBasis: arcBasisOf(arc) } : {}),
      /* The pair on this post is Brill's LAST words against hers — the only
         pairs voice learning reads (api/_lib/voiceLearn.js). */
      draftIs: DRAFT_IS_LAST,
    },
    ctaId: ctaRow ? ctaRow.id : null,
    offerId: offerObj ? offerObj.id : null,
    /* THE LINK GOES IN THE FIRST COMMENT, never the post (her rule). Only
       into an EMPTY comment — one she wrote is hers. */
    ...(link && !String(cur.firstComment || "").trim() ? { firstComment: link } : {}),
    /* The idea it came from is stamped when these words LAND in the post. */
    ...(choice?.idea ? { _idea: choice.idea } : {}),
    /* ITS PICTURE COMES WITH IT (her catch, 2026-09-29: "I picked it but it
       didn't bring the picture from the story over"). Only into a post with
       no picture — one she attached is hers — and never a testimonial's photo,
       which is the client's headshot, not a picture for the post. */
    ...(pickedPicture(choice?.idea) && !pictureOf(cur.visual) ? { visual: withPicture(cur.visual, { url: pickedPicture(choice.idea) }) } : {}),
  });

  return { body, patchFor };
}

/* WHAT A POST IN AN ARC WAS WRITTEN FROM (her ask, 2026-09-30): each earlier
   post's words, as `arcContext` handed them to Brill, reduced to a hash — the
   same one the Accelerator copies use to know they have changed (sourceHash).
   A post written before any earlier one had words gets an empty basis, so a
   later first draft of that earlier post still reads as a change. */
/* Marks a post whose captionDraft is Brill's last words (from 2026-09-30).
   Pairs recorded before, when it was Brill's FIRST draft, are not learned
   from: a later Brill rewrite would read as her edit. Mirrored in voiceLearn. */
export const DRAFT_IS_LAST = "last";

export function arcBasisOf(arc) {
  return Object.fromEntries((arc?.earlier || []).map((e) => [String(e.n), hashSource(e.text)]));
}

/* The picture a Library pick brings to a post: a story's or an idea's, never
   a testimonial's headshot. A link, as the Library stores it. */
export function pickedPicture(item) {
  if (!item || item.kind === "testimonial") return null;
  const url = String(item.photo || "").trim();
  return /^https?:\/\/\S+$/i.test(url) ? url : null;
}

/* "PICK FROM LIBRARY" (her design, 2026-10-01: "pick it from library with
   your words. It will just copy it"). The kinds that are HER words — an idea
   or a story; a testimonial is a client's, an objection is a question. */
export const OWN_WORDS_KINDS = ["idea", "story"];

/** The patch that puts a Library entry's words into an EMPTY post, word for
 *  word — no Brill, so no cost. Brill's earlier draft record is CLEARED: the
 *  words are hers now, and a stale `captionDraft` against them would read to
 *  voice learning as her rewriting Brill (api/_lib/voiceLearn.js). Its picture
 *  comes too, only into a post without one. The item rides as `_idea` so the
 *  caller stamps it used, as a draft does. */
export function fromLibraryPatch(cur, item) {
  const { draftIs, ...inputs } = cur?.inputs || {};
  void draftIs;
  const pic = pickedPicture(item);
  return {
    caption: String(item?.text || "").trim(),
    captionDraft: null,
    inputs,
    ...(pic && !pictureOf(cur?.visual) ? { visual: withPicture(cur?.visual, { url: pic }) } : {}),
    _idea: item,
  };
}

/* The text of Brill's answer to a `kind: "post"` call, or "". */
export const writtenText = (data) => data?.content?.find((b) => b.type === "text")?.text?.trim() || "";


/* ── SAVE AS ANOTHER PLATFORM (her idea, 2026-10-06) ─────────────────────────
 * "Take the story and change it for a different format." Her decisions: a
 * SEPARATE post, linked to the one it came from, and "one won't change the
 * other" — so there is no drift note between versions, unlike an arc's posts.
 * A family is flat: every version points at the FIRST post (`sourcePostId`),
 * so a Facebook version saved from the Instagram one still sits beside both.
 */

/** The request for Brill's version of `source` on `platform`.
 *  `length` (a POST_LENGTHS id), `hook` ("keep" or a hook category id) and
 *  `note` (her line, "a little punchier") come from the Save as pop-up
 *  (2026-10-09); each is left out when not chosen, so the old request stands. */
export function adaptRequest(source, platform, { length, hook, note } = {}) {
  const about = String(pictureOf(source?.visual)?.about || "").trim();
  const line = String(note || "").trim();
  return {
    kind: "adapt",
    platform,
    fromPlatform: platformOf(source?.platform).id,
    post: String(source?.caption || ""),
    ...(about ? { picture: about } : {}),
    ...(length ? { length } : {}),
    ...(hook ? { hookCategory: hook } : {}),
    ...(line ? { note: line } : {}),
  };
}

/** ONE LINE (her ask, 2026-10-09: "what if on instagram they just want one
 *  line"). Save as only for now; the Draft pop-up keeps POST_LENGTHS. Mirrored
 *  in prompts.js buildSocialAdaptPrompt. */
export const ONE_LINE = { id: "line", label: "One line", words: "one sentence" };

/** The lengths Save as offers for a platform, as LengthChoice options.
 *  Instagram stops at Medium: it cuts a caption off at 2,200 characters, and
 *  Long (300–450 words) runs past it. prompts.js caps Instagram at 250 words
 *  whatever arrives. */
export const saveAsLengths = (platform) => [ONE_LINE, ...POST_LENGTHS.filter((l) => platform !== "instagram" || l.id !== "long")];

/** The new post. `caption` is Brill's version, or — for a plain copy — the
 *  same words. What the post IS carries over (story, picture, offer, pillar,
 *  the link, the first comment); what belongs to the first post's life does
 *  not: its GHL draft, its account, its place in an arc, its Done. */
export function savedAsPost(source, { id, platform, caption, plain = false, length = null, hook = null }) {
  const keep = { ...(source?.inputs || {}) };
  for (const k of ["ghlPostId", "ghlAccountId", "arcSlot", "arcBasis", "context", ...(plain ? ["draftIs"] : [])]) delete keep[k];
  const root = source?.inputs?.sourcePostId || source?.id || null;
  const words = String(caption || "");
  return {
    id,
    platform: plain ? source?.platform || platform : platform,
    title: deriveTitle(words),
    caption: words,
    /* Brill's words are Brill's draft, so voice learning measures her edits
       from them; a plain copy is HER words, never a draft. */
    captionDraft: plain ? null : words,
    firstComment: source?.firstComment || "",
    visual: source?.visual || null,
    offerId: source?.offerId || null,
    ctaId: source?.ctaId || null,
    status: "draft",
    inputs: {
      ...keep,
      /* The length and hook she picked in the pop-up are the post's own from
         here, so the cog and a later redraft start from them. One line is a
         Save as shape only: the post itself remembers Short. */
      ...(plain ? {} : { draftIs: DRAFT_IS_LAST, length: POST_LENGTHS.some((l) => l.id === length) ? length : length === ONE_LINE.id ? "short" : defaultLength(platform), ...(hook && hook !== "keep" ? { hookCategory: hook } : {}) }),
      sourcePostId: root,
    },
    isNew: true,
  };
}

/** The other versions of this post, for "Also: LinkedIn · Facebook" — the
 *  first post and everything saved from it, never this one. */
export function familyOf(post, posts = []) {
  if (!post) return [];
  const root = post.inputs?.sourcePostId || post.id;
  return (posts || []).filter((p) => p && p.id !== post.id && (p.id === root || p.inputs?.sourcePostId === root));
}

/** Brill's version, tidied. The replays (2026-10-06) kept putting "Hashtags:"
 *  in front of the tags even when told not to — a label she would have to
 *  delete before posting — so it comes off here, every time, at no cost. */
export function cleanAdapted(text) {
  return String(text || "").replace(/^[ \t]*hashtags:[ \t]*/gim, "").trim();
}
